import { useState, useMemo, useRef, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import InstallPrompt from "@/components/InstallPrompt";
import Sidebar from "@/components/Sidebar";
import MobileNav from "@/components/MobileNav";
import Header from "@/components/Header";
import RecentlyAdded from "@/components/RecentlyAdded";
import DriveHealthBanner from "@/components/DriveHealthBanner";
import AppLayout from "@/components/AppLayout";
import SectionPlaceholder from "@/components/SectionPlaceholder";
import DynamicDashboard from "@/pages/dashboards/DynamicDashboard";
import RolesAccess from "@/pages/RolesAccess";
import AuditLog from "@/pages/AuditLog";
import ConnectionsPage from "@/pages/ConnectionsPage";
import ModulesPage from "@/pages/ModulesPage";
import BrandAssets from "@/pages/BrandAssets";
import TrashReset from "@/pages/TrashReset";
import Directory from "@/pages/membership/Directory";
import Visitors from "@/pages/membership/Visitors";
import Contacts from "@/pages/membership/Contacts";
import CodesPins from "@/pages/membership/CodesPins";
import { RAIL_GROUPS, slugOf, idOfSlug, pageOf } from "@/lib/navLayout";
import AccountLinks from "@/pages/membership/AccountLinks";
import DuesSettings from "@/pages/membership/DuesSettings";
import GardenPlots from "@/pages/gardens/GardenPlots";
import GardenHours from "@/pages/gardens/GardenHours";
import ReviewQueue from "@/pages/reimbursements/ReviewQueue";
import BoardMeetings from "@/pages/governance/BoardMeetings";
import VotesApprovals from "@/pages/governance/VotesApprovals";
import MyTasks from "@/pages/governance/MyTasks";
import EventsAdmin from "@/pages/events/EventsAdmin";
import Partnerships from "@/pages/partnerships/Partnerships";
import PointsRewards from "@/pages/points/PointsRewards";
import MerchInventory from "@/pages/inventory/MerchInventory";
import Fulfillment from "@/pages/fulfillment/Fulfillment";
import GrantsAdmin from "@/pages/grants/GrantsAdmin";
import TargetedMessages from "@/pages/messaging/TargetedMessages";
import NotificationSettings from "@/pages/settings/NotificationSettings";
import UnifiedCalendar from "@/components/UnifiedCalendar";
import LegacyBricks from "@/pages/bricks/LegacyBricks";
import Donations from "@/pages/donations/Donations";
import CheckInStation from "@/pages/checkin/CheckInStation";
import MemberDashboard from "@/pages/dashboards/MemberDashboard";
import DigitalCard from "@/components/DigitalCard";
import MyMemberships from "@/components/MyMemberships";
import IncompleteSignups from "@/pages/membership/IncompleteSignups";
import MyAccount from "@/pages/membership/MyAccount";
import GivePage from "@/pages/donations/GivePage";
import MemberRewards from "@/pages/points/MemberRewards";
import PaymentHistory from "@/pages/membership/PaymentHistory";
import MyVolunteering from "@/pages/events/MyVolunteering";
import MemberEvents from "@/pages/events/MemberEvents";
import MyMessages from "@/components/MyMessages";

const ComingSoon = ({ title, note }) => (
  <div data-testid={`coming-soon-${title.toLowerCase()}`}>
    <h1 className="font-zilla text-3xl sm:text-4xl font-semibold mb-2">{title}</h1>
    <div className="bg-cream border border-ink/15 rounded-xl p-10 text-center">
      <div className="text-[15px] text-ink/70 max-w-md mx-auto">{note}</div>
      <div className="mt-3 inline-block text-[12px] font-semibold px-3 py-1 rounded-full bg-gold/25 text-ink">Coming soon</div>
    </div>
  </div>
);

function DashboardContent({ me, onNavigate }) {
  return <DynamicDashboard me={me} onNavigate={onNavigate} />;
}

// "#membership/directory" (group/page) or an old single-name link like "#membership_directory".
const fromHash = (h) => { const parts = (h || "").replace(/^#/, "").split("/"); return idOfSlug(parts[parts.length - 1] || "") || "dashboard"; };
const toHash = (id) => { const g = RAIL_GROUPS.find((x) => x.pages.includes(pageOf(id))); return g ? `${g.id}/${slugOf(id)}` : slugOf(id); };

export default function Workspace() {
  const { me } = useAuth();
  const [active, setActive] = useState(() => (typeof window !== "undefined" && window.location.hash ? fromHash(window.location.hash) : "dashboard"));
  const [meetingOpenId, setMeetingOpenId] = useState(null);
  useEffect(() => {
    const nav = (e) => { if (e.detail?.section) setActive(e.detail.section); };
    const onHash = () => { const h = window.location.hash.slice(1); if (h) setActive(fromHash(h)); };
    window.addEventListener("rca:navigate", nav);
    window.addEventListener("hashchange", onHash);
    return () => { window.removeEventListener("rca:navigate", nav); window.removeEventListener("hashchange", onHash); };
  }, []);
  useEffect(() => { if (active && window.location.hash.slice(1) !== toHash(active)) window.location.hash = toHash(active); }, [active]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mainRef = useRef(null);

  // Reset the main content scroll to the top on every navigation.
  useEffect(() => { mainRef.current?.scrollTo(0, 0); }, [active]);

  const navIds = useMemo(() => (me?.nav || []).map((n) => n.id), [me]);

  // App layout for general members and board members.
  if (me.role.layout === "app") {
    return <AppLayout me={me} />;
  }

  // If active section is no longer in nav (persona switch), fall back to dashboard.
  const current = navIds.includes(active) ? active : "dashboard";
  const section = (me.sections || []).find((s) => s.id === current);

  const renderContent = () => {
    switch (current) {
      case "dashboard":
        return <DashboardContent me={me} onNavigate={setActive} />;
      case "roles_access":
        return <RolesAccess me={me} />;
      case "audit_log":
        return <AuditLog />;
      case "connections":
        return <ConnectionsPage />;
      case "modules":
        return <ModulesPage />;
      case "brand_assets":
        return <BrandAssets />;
      case "calendar":
        return <UnifiedCalendar onOpenMeeting={(id) => { setMeetingOpenId(id); setActive("board_meetings"); }} />;
      case "trash_reset":
        return <TrashReset me={me} />;
      case "membership_directory":
        return <Directory />;
      case "visitors":
        return <Visitors />;
      case "contacts":
        return <Contacts />;
      case "paused_removed":
        return <Directory initialStatus="suspended" />;
      case "membership_codes":
      case "member_pins":
        return <CodesPins active={current} allowed={navIds} onTab={setActive} />;
      case "incomplete_signups":
        return <IncompleteSignups />;
      case "account_links":
        return <AccountLinks />;
      case "dues_settings":
        return <DuesSettings />;
      case "garden_plots":
        return <GardenPlots />;
      case "garden_hours":
        return <GardenHours />;
      case "reimbursements_queue":
        return <ReviewQueue />;
      case "board_meetings": {
        const pending = meetingOpenId || sessionStorage.getItem("rca_open_meeting");
        if (pending) sessionStorage.removeItem("rca_open_meeting");
        return <BoardMeetings openId={pending} />;
      }
      case "votes_approvals":
        return <VotesApprovals />;
      case "my_tasks":
        return <MyTasks />;
      case "events":
        return <EventsAdmin me={me} />;
      case "partnerships":
        return <Partnerships />;
      case "points_rewards":
        return <PointsRewards me={me} />;
      case "merch_inventory":
        return <MerchInventory me={me} />;
      case "legacy_bricks":
        return <LegacyBricks />;
      case "donations":
        return <Donations me={me} />;
      case "fulfillment":
        return <Fulfillment me={me} />;
      case "targeted_messages":
        return <TargetedMessages me={me} />;
      case "notification_settings":
        return <NotificationSettings me={me} />;
      case "grants":
        return <GrantsAdmin me={me} />;
      case "checkin":
        return <CheckInStation />;
      case "documents":
        return <ComingSoon title="Documents" note="Board document library — reused from the admin side and filtered to what board members should see. Wiring lands with the shared document store." />;
      case "partnerships":
        return <ComingSoon title="Partnerships" note="Coming soon. Once the Partnerships phase ships, this reuses and filters the admin Partnerships records the same way Documents will." />;
      case "events":
        return <ComingSoon title="Events" note="Coming soon. This will point at the same shared community calendar everyone sees — not a separate board-only calendar." />;
      case "gardeners":
        return <Directory lockedTag="Gardener" title="Gardeners" hideAdd showDues testid="gardeners-page"
                 subtitle="Members in the 100th Street Gardens program — a filtered view of the membership directory." />;
      case "mm_overview":
        return <MemberDashboard me={me} onNavigate={(id) => navIds.includes(id) && setActive(id)} />;
      case "mm_card":
        return (
          <div data-testid="my-card-page">
            <h1 className="font-zilla text-3xl font-semibold mb-1">My Card</h1>
            <p className="text-[16px] text-ink/70 mb-5">Your official Rosemoor digital membership card.</p>
            <DigitalCard />
            <MyMemberships />
          </div>
        );
      case "mm_account":
        return <MyAccount />;
      case "mm_give":
        return <GivePage prefill={{ name: me?.user?.name, email: me?.user?.email }} />;
      case "mm_rewards":
        return <MemberRewards />;
      case "mm_payments":
        return <PaymentHistory />;
      case "mm_volunteering":
        return <MyVolunteering />;
      case "mm_events":
        return <MemberEvents />;
      case "mm_messages":
        return <MyMessages asPage />;
      default:
        return <SectionPlaceholder section={section} me={me} />;
    }
  };

  return (
    <div className="h-screen overflow-hidden flex flex-col bg-warm-cream">
      <InstallPrompt />
      <div className="flex flex-1 min-h-0">
        <Sidebar
          me={me}
          active={current}
          onSelect={(id) => { setActive(id); setSidebarOpen(false); }}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          <Header me={me} onSelect={setActive} />
          <main ref={mainRef} className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 lg:p-8 pb-28 min-[900px]:pb-8" data-testid="main-content">
            <div key={current} className="animate-rise w-full max-w-[1200px] mx-auto">
              {(me.is_super_admin || me.user?.is_super_admin) && <DriveHealthBanner />}
              <RecentlyAdded />
              {renderContent()}
            </div>
          </main>
        </div>
      </div>
      <MobileNav me={me} active={current} onSelect={setActive} />
    </div>
  );
}
